import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CardContent, CardHeader, IconButton, MenuItem, InputLabel, Select, Tooltip, Grid2, Badge, Typography, Chip, TextField, FormControl, Button, Alert } from '@mui/material';

import ClearIcon from '@mui/icons-material/Clear';

import SourcesList from './SourcesList';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import RuleBuilder from './RuleBuilder';

const VAR_REGEX = /(?<type>@|\$)\{(?<path>[^}]+)\}/g;

const DEFAULT_ACTIONS = {
    OnSuccess: {
        Name: 'OutputExpression',
        Context: {
            Expression: ''
        }
    },
    OnFailure: {
        Name: 'OutputExpression',
        Context: {
            Expression: ''
        }
    }
}

const SUCCESS_ACTIONS = [
    { label: 'Continue', value: 'CONTINUE' },
    { label: 'System log', value: 'SYSTEM_LOG' },
]

const FAILURE_ACTIONS = [
    { label: 'Continue', value: 'CONTINUE' },
    { label: 'System log', value: 'SYSTEM_LOG' },
    { label: 'Create a Task', value: 'CREATE_TASK' }
]

export default function RuleSet() {
    const { ruleSetId } = useParams();
    const [loading, setLoading] = useState(true);
    const [loadingSources, setLoadingSources] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorSaving, setErrorSaving] = useState(null);
    const [error, setError] = useState(null);
    const [ruleSet, setRuleSet] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogSignal, setDialogSignal] = useState(0);
    const [refreshSources, setRefreshSources] = useState(0);
    const [rules, setRules] = useState([]);
    const [rulesRaw, setRulesRaw] = useState([]);
    const [edit, setEdit] = useState(false);
    const [sources, setSources] = useState([]);
    const [versions, setVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(null);

    // API calls
    const fetchRules = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/rules/${ruleSetId}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            const json = await res.json();
            setRuleSet(json);
            setSelectedVersion(json.version);
            setRules(JSON.parse(json.rules) || []);
            setRulesRaw(JSON.parse(json.rulesRaw) || []);
            setError(null);
        } catch (error) {
            setError(error);
            console.error('Error fetching rules:', error);
        } finally {
            setLoading(false);
        }
    }

    const fetchSpecificVersion = async (version) => {
        setLoading(true);
        try {
            const res = await fetch(`/api/rules/${ruleSetId}/${version}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            const json = await res.json();
            setRuleSet(json);
            setSelectedVersion(json.version);
            setRules(JSON.parse(json.rules) || []);
            setRulesRaw(JSON.parse(json.rulesRaw) || []);
            setError(null);
        } catch (error) {
            setError(error);
            console.error('Error fetching specific version:', error);
        } finally {
            setLoading(false);
        }
    }

    const fetchAllVersions = async () => {
        try {
            const res = await fetch(`/api/rules/${ruleSetId}/versions`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            const json = await res.json();
            setVersions(json);
        } catch (error) {
            console.error('Error fetching versions:', error);
            setVersions([]);
        }
    }

    const fetchAllSources = async () => {
        setLoadingSources(true);
        try {
            const res = await fetch(`/api/ruleSet/${ruleSetId}/sources/all`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            let json = await res.json();
            json = json.map(s => {
                return {
                    ...s,
                    fields: s.fields.replace(VAR_REGEX, '0'),
                }
            });
            setSources(json);
        } catch (error) {
            console.error('Error fetching sources:', error);
            setSources([]);
        } finally {
            setLoadingSources(false);
        }
    }

    const saveRules = async () => {
        setSaving(true);
        try {
            const res = await fetch(`/api/rules/${ruleSetId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...ruleSet, rules: rules, rulesRaw: rulesRaw })
            });
            if (!res.ok) {
                throw new Error(`Error saving rules: ${res.statusText}`);
            }
            setErrorSaving(null);
            setEdit(false);
        } catch (error) {
            setErrorSaving(error);
            console.error('Error saving rules:', error);
        } finally {
            setSaving(false);
        }
    }

    const createNewVersion = async () => {
        setSaving(true);
        try {
            const res = await fetch(`/api/rules/${ruleSetId}/new-version`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rules: rules, rulesRaw: rulesRaw })
            });
            if (!res.ok) {
                throw new Error(`Error creating new version: ${res.statusText}`);
            }
            setErrorSaving(null);
            setEdit(false);
            fetchRules();
            fetchAllVersions();
        } catch (error) {
            setErrorSaving(error);
            console.error('Error creating new version:', error);
        } finally {
            setSaving(false);
        }
    }

    const setRuleAsActive = async (version) => {
        setLoading(true);
        try {
            const res = await fetch(`/api/rules/${ruleSetId}/set-active`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ version: version })
            });
            if (!res.ok) {
                throw new Error(`Error setting rule as active: ${res.statusText}`);
            }
            fetchAllVersions();
            fetchRules();
        } catch (error) {
            console.error('Error setting rule as active:', error);
        }
    }

    // Effects
    useEffect(() => {
        if (ruleSetId) {
            fetchRules();
            fetchAllVersions();
        }
    }, [ruleSetId]);

    useEffect(() => {
        fetchAllSources();
    }, [refreshSources]);

    const handleEditClick = () => {
        setEdit(true);
    }

    // Handlers
    const handleChangeRuleSet = (field, value) => {
        setRuleSet(prev => ({ ...prev, [field]: value }));
    }

    const handleSelectVersionChange = (event) => {
        const version = event.target.value;
        if (version !== selectedVersion) {
            fetchSpecificVersion(version);
        }
    };

    const handleChangeRule = (index, field, value) => {
        if (field == 'RuleName') {
            // also update rulesRaw
            setRulesRaw(prev => {
                const newRulesRaw = [...prev];
                newRulesRaw[index] = { ...newRulesRaw[index], RuleName: value };
                return newRulesRaw;
            });
        }
        setRules(prev => {
            const newRules = [...prev];
            newRules[index] = { ...newRules[index], [field]: value };
            return newRules;
        });
    }

    const handleRuleRawChange = (RuleName, newModel) => {
        setRulesRaw(prev => {
            const newRulesRaw = prev.map(r => r.RuleName === RuleName ? { ...r, condition: newModel } : r);
            return newRulesRaw;
        });
    }

    const handleAddNewRule = () => {
        setRules(prev => ([...prev, { RuleName: '', Expression: '', errorMessage: '', enabled: true, Actions: DEFAULT_ACTIONS }]));
        setRulesRaw(prev => ([...prev, { RuleName: '', condition: null }]));
    }

    const handleSaveClick = async () => {
        await saveRules();
    }

    const handleCreateNewVersionClick = async () => {
        await createNewVersion();
    }

    const handleSetAsActiveClick = async () => {
        await setRuleAsActive(selectedVersion);
    }

    const handleDeleteRule = (index) => {
        setRules(prev => prev.filter((_, i) => i !== index));
        setRulesRaw(prev => prev.filter((_, i) => i !== index));
    }

    const handleActionExpressionChange = (index, actionType, value) => {
        setRules(prev => {
            const newRules = [...prev];
            const rule = newRules[index];
            if (rule) {
                rule.Actions = {
                    ...rule.Actions,
                    [actionType]: {
                        ...rule.Actions[actionType],
                        context: {
                            ...rule.Actions[actionType]?.context,
                            Expression: value
                        }
                    }
                };
            }
            return newRules;
        });
    }

    return (
        <Box>
            <Grid2 container columns={16}>
                <Grid2 item size={11} sx={{ borderRight: '1px solid #ccc', padding: 2, height: 'calc(100vh - 77px)', overflowY: 'auto' }}>
                    {(loading || loadingSources) ? <CircularProgress /> : <>
                        {error && (
                            <Alert severity="error">
                                {error.message}
                            </Alert>
                        )}
                        {/* Read mode */}
                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                            <InputLabel id="rule-set-version-select-label">Version</InputLabel>
                            <Select
                                labelId="rule-set-version-select-label"
                                id="rule-set-version-select-select"
                                value={selectedVersion}
                                label="Version"
                                onChange={handleSelectVersionChange}
                                disabled={loading || versions.length === 0 || edit}
                            >
                                {versions.map((v, index) => (
                                    <MenuItem key={index} value={v.version}>v{v.version} {v.active ? '(Active)' : ''}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {ruleSet && !edit && error == null && (
                            <>
                                <Typography variant="h4" gutterBottom>
                                    {ruleSet.name} (v{ruleSet.version})
                                    <span style={{ float: 'right' }}>
                                        <IconButton onClick={handleEditClick}>
                                            <EditIcon />
                                        </IconButton>
                                    </span>
                                </Typography>
                                <Button size="small" onClick={() => navigator.clipboard.writeText(ruleSetId)}>Version Id: {ruleSet.versionId}</Button>
                                <Box>
                                    {
                                        ruleSet.active
                                            ? <Chip label="Active" size="small" color="success" />
                                            : <Chip label="Inactive" size="small" color="error" />
                                    }
                                    {ruleSet.active == false && <Button variant='text' onClick={handleSetAsActiveClick}>Set as Active</Button>}
                                </Box>
                                <hr />
                                <Typography variant="body1" gutterBottom>
                                    {ruleSet ? ruleSet.description : ''}
                                </Typography>
                                <Box mt={2}>
                                    {rules?.map((rule, index) => (
                                        <Box key={rule.RuleName + index} mb={3} p={2} border="1px solid #ccc" borderRadius="8px">
                                            <Typography variant="body2"><b>Rule name:</b> {rule.RuleName}</Typography>
                                            <Typography variant="body2" color="textSecondary"><b>Error Message:</b> {rule.ErrorMessage}</Typography>
                                            <hr />
                                            <Typography variant="body2" gutterBottom>
                                                <b>Success action:</b> {SUCCESS_ACTIONS.find(a => a.value === rule.Actions?.OnSuccess?.Context?.Expression)?.label}
                                            </Typography>
                                            <Typography variant="body2" gutterBottom>
                                                <b>Failure action:</b> {FAILURE_ACTIONS.find(a => a.value === rule.Actions?.OnFailure?.Context?.Expression)?.label}
                                            </Typography>
                                            <hr />
                                            <Typography variant="body2" gutterBottom><b>Rule:</b></Typography>
                                            <RuleBuilder
                                                items={sources}
                                                rule={rulesRaw.find(r => r.RuleName == rule.RuleName)?.condition}
                                                readonly={true}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                        {/* Edit mode */}
                        {ruleSet && edit && error == null && (
                            <>
                                <Typography variant="h4" gutterBottom>
                                    Edit Rule Set
                                </Typography>
                                <small>{ruleSet ? ruleSet.id : ''}</small><br />
                                <hr />
                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        label="Name"
                                        value={ruleSet.name}
                                        variant="outlined"
                                        onChange={(e) => handleChangeRuleSet('name', e.target.value)}
                                    />
                                </FormControl>
                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        label="Description"
                                        value={ruleSet.description}
                                        variant="outlined"
                                        onChange={(e) => handleChangeRuleSet('description', e.target.value)}
                                    />
                                </FormControl>
                                <Box mt={2} mb={2} p={1} >
                                    {rules?.map((rule, index) => (
                                        <Badge
                                            color=''
                                            key={rule.RuleName + index}
                                            sx={{ width: '100%' }}
                                            badgeContent={
                                                <ClearIcon fontSize='medium' onClick={() => handleDeleteRule(index)} style={{ padding: '2px', cursor: 'pointer', color: 'white', backgroundColor: 'red', borderRadius: '3rem' }} />
                                            }>
                                            <Box key={index} mb={3} p={2} border="1px solid #ccc" borderRadius="8px" width="100%">
                                                <TextField
                                                    label="Rule Name"
                                                    value={rule.RuleName}
                                                    variant="outlined"
                                                    fullWidth
                                                    onChange={(e) => handleChangeRule(index, 'RuleName', e.target.value)}
                                                />
                                                <TextField
                                                    label="Error Message"
                                                    value={rule.errorMessage}
                                                    variant="outlined"
                                                    fullWidth
                                                    onChange={(e) => handleChangeRule(index, 'errorMessage', e.target.value)}
                                                    style={{ marginTop: '10px' }}
                                                />
                                                <FormControl fullWidth margin="normal">
                                                    <InputLabel id={`rule-success-action-select-label-${index}`}>Success action</InputLabel>
                                                    <Select
                                                        labelId={`rule-success-action-select-label-${index}`}
                                                        id={`rule-success-action-select-input-${index}`}
                                                        value={rule.Actions?.OnSuccess?.context?.Expression ?? SUCCESS_ACTIONS[0].value}
                                                        label="Success action"
                                                        onChange={(e) => handleActionExpressionChange(index, 'OnSuccess', e.target.value)}
                                                    >
                                                        {SUCCESS_ACTIONS.map((action, index) => (
                                                            <MenuItem key={action.value + index} value={action.value}>{action.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <FormControl fullWidth margin="normal">
                                                    <InputLabel id={`rule-failure-action-select-label-${index}`}>Failure action</InputLabel>
                                                    <Select
                                                        labelId={`rule-failure-action-select-label-${index}`}
                                                        id={`rule-failure-action-select-input-${index}`}
                                                        value={rule.Actions?.OnFailure?.context?.Expression ?? FAILURE_ACTIONS[0].value}
                                                        label="Failure action"
                                                        onChange={(e) => handleActionExpressionChange(index, 'OnFailure', e.target.value)}
                                                    >
                                                        {FAILURE_ACTIONS.map((action, index) => (
                                                            <MenuItem key={action.value + index} value={action.value}>{action.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <RuleBuilder
                                                    items={sources}
                                                    onChange={
                                                        (val, raw) => {
                                                            handleChangeRule(index, 'Expression', val);
                                                            handleRuleRawChange(rule.RuleName, raw);
                                                        }
                                                    }
                                                    rule={rulesRaw.find(r => r.RuleName == rule.RuleName)?.condition}
                                                />
                                            </Box>
                                        </Badge>
                                    ))}
                                    {!saving && (
                                        <Button variant="outlined" onClick={handleAddNewRule}>Add New Rule</Button>
                                    )}
                                </Box>
                                {saving && <CircularProgress />}
                                {errorSaving && (
                                    <Alert severity="error" style={{ marginBottom: '10px' }}>
                                        {errorSaving.message}
                                    </Alert>
                                )}
                                {!saving && (
                                    <Box>
                                        <Box>
                                            <Button variant="contained" color="primary" onClick={handleSaveClick}>
                                                Save
                                            </Button>
                                            <Button variant="contained" color="primary" onClick={handleCreateNewVersionClick} style={{ marginLeft: '10px' }}>
                                                Save as new version
                                            </Button>
                                            <Button variant="outlined" onClick={() => { setEdit(false); fetchRules(); }} style={{ marginLeft: '10px' }}>
                                                Cancel
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </>}
                </Grid2>
                <Grid2 item size={5}>
                    <Box >
                        <CardHeader
                            title="Sources"
                            action={
                                <Tooltip title="Add Source">
                                    <IconButton size="small" onClick={() => setDialogSignal(prev => prev + 1)}>
                                        <AddIcon />
                                    </IconButton>
                                </Tooltip>
                            }
                        />
                        <CardContent>
                            <SourcesList ruleSetId={ruleSetId} refreshSources={refreshSources} setRefreshSources={setRefreshSources} dialogOpen={dialogOpen} setDialogOpen={setDialogOpen} dialogSignal={dialogSignal} />
                        </CardContent>
                    </Box>
                </Grid2>
            </Grid2>
        </Box>
    );
}
